import { LinkListModuleType } from "@/modules/LinkListModule/LinkListModule";
import { WPClient } from "@/wp/client/WPClient";
import { LinkListBoxElementType } from "@/modules/LinkListModule/LinkListBox/LinkListBox";
import { Modul_Linkliste } from "@/wp/types/acf-module-types";

type LinkListElement = Exclude<Modul_Linkliste["linklist"]["elements"], false>;

interface FetchLinkListItemsProps {
  page: number;
  postsPerPage: number;
  offset: number;
  locale: string | undefined;
  defaultLocale: string | undefined;
  module: LinkListModuleType;
  isNobleElements: boolean;
  labels: {
    jobsBtn: string;
    insightsBtn: string;
    pressBtn: string;
    teamBtn: string;
    eventsBtn: string;
  };
}

const DEFAULT_LINK_EXTENDED: LinkListElement[number]["link_extended"] = {
  type: "link",
  link: null,
  download: {
    link_target: false,
    link_text: "",
    link_aria_label: "",
  },
  email: {
    link_target: "",
    link_text: "",
    link_aria_label: "",
  },
  telephone: {
    link_target: "",
    link_text: "",
    link_aria_label: "",
  },
  pop_up: {
    link_text: "",
    link_aria_label: "",
  },
};

export const fetchLinkListItems = async ({
  module,
  page,
  postsPerPage,
  offset,
  locale,
  defaultLocale,
  isNobleElements,
  labels,
}: FetchLinkListItemsProps): Promise<{
  posts: LinkListBoxElementType[];
  totalPosts: number;
}> => {
  const { type, jobs, insights, press, events, team, location, elements } =
    module.linklist;

  if (type === "manual") {
    const staticElements = (elements || []) as LinkListBoxElementType[];
    return { posts: staticElements, totalPosts: staticElements.length };
  }

  const client = new WPClient({ locale, defaultLocale });

  /* jscpd:ignore-start */
  if (type === "jobs") {
    const hasSpecificIds = Array.isArray(jobs) && jobs.length > 0;
    const hasLocation = Array.isArray(location) && location.length > 0;

    if (!hasSpecificIds && !hasLocation) {
      return { posts: [], totalPosts: 0 };
    }

    const locationIds = Array.isArray(location) ? location : [];

    const result = await client.getPosts({
      type: "jobs",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_job.title",
        "acf.header_job.overline",
        "acf.header_job.icon",
      ],
      ...(hasSpecificIds
        ? { includeIds: { ids: jobs, keepOrder: true } }
        : {
            includeTaxonomies: [
              { taxonomy: "location_category", ids: locationIds },
            ],
          }),
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_job;
      return {
        svg: header?.icon || false,
        tagline: header?.overline || "",
        title: header?.title || "",
        link: {
          title: labels.jobsBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies LinkListBoxElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "insights") {
    const hasSpecificIds = Array.isArray(insights) && insights.length > 0;

    if (!hasSpecificIds) {
      return { posts: [], totalPosts: 0 };
    }

    const result = await client.getPosts({
      type: "blog",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_post.title",
        "acf.header_post.media.image_desktop",
        "date",
        "_embedded",
        "location_category",
      ],
      includeIds: { ids: insights, keepOrder: true },
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_post;

      const locationSlugs: string[] = post._embedded?.["wp:term"]?.[1]
        ? post._embedded["wp:term"][1].map((t) => (t as { slug: string }).slug)
        : [];

      const itemIsNE = !locationSlugs.includes("noble-bc");

      const resolvedUrl = (() => {
        if (!post.link) return post.link;
        try {
          const url = new URL(post.link);
          const base = itemIsNE
            ? (process.env.NEXT_PUBLIC_PUBLIC_URL_ELEMENTS ?? url.origin)
            : (process.env.NEXT_PUBLIC_PUBLIC_URL ?? url.origin);
          return base + url.pathname + url.search + url.hash;
        } catch {
          return post.link;
        }
      })();

      const isCrossDomain = isNobleElements !== itemIsNE;

      return {
        svg: header.media.image_desktop,
        tagline: new Date(post.date).toLocaleDateString(locale, {
          year: "numeric",
          month: "2-digit",
          day: "2-digit",
        }),
        title: header?.title || "",
        link: {
          title: labels.insightsBtn,
          url: resolvedUrl,
          target: (isCrossDomain ? "_blank" : "") as "_blank" | "",
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
        location: locationSlugs,
      } satisfies LinkListBoxElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "press") {
    const hasSpecificIds = Array.isArray(press) && press.length > 0;

    if (!hasSpecificIds) {
      return { posts: [], totalPosts: 0 };
    }

    const result = await client.getPosts({
      type: "press",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_press.title",
        "acf.header_press.image",
      ],
      includeIds: { ids: press, keepOrder: true },
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_press;
      return {
        svg: header.image,
        tagline: "",
        title: header?.title || "",
        link: {
          title: labels.pressBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies LinkListBoxElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "team") {
    const hasSpecificIds = Array.isArray(team) && team.length > 0;

    if (!hasSpecificIds) {
      return { posts: [], totalPosts: 0 };
    }

    const result = await client.getPosts({
      type: "team",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_team.image",
        "acf.header_team.firstname",
        "acf.header_team.name",
        "acf.header_team.position",
        "acf.header_team.content",
      ],
      includeIds: { ids: team, keepOrder: true },
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_team;
      return {
        svg: header?.image || false,
        tagline: header?.position || "",
        title: `${header?.firstname || ""} ${header?.name || ""}`.trim(),
        content: header?.content || "",
        link: {
          title: labels.teamBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies LinkListBoxElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }

  if (type === "events") {
    const hasSpecificIds = Array.isArray(events) && events.length > 0;

    if (!hasSpecificIds) {
      return { posts: [], totalPosts: 0 };
    }

    const result = await client.getPosts({
      type: "events",
      page,
      perPage: postsPerPage,
      offset,
      fields: [
        "id",
        "link",
        "acf.header_events.title",
        "acf.header_events.media.image_desktop",
        "acf.header_events.date",
      ],
      includeIds: { ids: events, keepOrder: true },
    });

    const posts = result.posts.map((post) => {
      const header = post.acf?.header_events;
      return {
        svg: header?.media?.image_desktop || false,
        tagline: header?.date
          ? new Date(header.date).toLocaleDateString(locale, {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
            })
          : "",
        title: header?.title || "",
        link: {
          title: labels.eventsBtn,
          url: post.link,
          target: "" as const,
          aria_label: "",
        },
        link_extended: DEFAULT_LINK_EXTENDED,
      } satisfies LinkListBoxElementType;
    });

    return { posts, totalPosts: result.totalPosts };
  }
  /* jscpd:ignore-end */

  return { posts: [], totalPosts: 0 };
};
